"use client";

import FocusTrap from "focus-trap-react";
import { usePathname, useSearchParams } from "next/navigation";
import { PropsWithChildren, useCallback, useEffect, useState } from "react";
import { SiteIdentityMobileLogo } from "./assets";
import { MobileNavigationButton } from "./items";
import { Breakpoint, FontWeight, NavLogoKind } from "./types";

export interface IMobileNavBarProps {
	readonly siteIdentityLinkLogo: NavLogoKind;
	readonly siteIdentityLinkTooltip: string;
	readonly mobileBreakpoint: Breakpoint;
	readonly itemsFontWeight: FontWeight;
}
const MobileNavBar: React.FC<PropsWithChildren<IMobileNavBarProps>> = ({
	siteIdentityLinkLogo,
	siteIdentityLinkTooltip,
	children,
	mobileBreakpoint,
	itemsFontWeight,
}) => {
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const onMobileNavigationChange = useCallback((open: boolean) => {
		document.body.style.overflowY = open ? "hidden" : "";
	}, []);
	const [mobileNavigationOpen, setMobileNavigationOpen] = useState<boolean>(false);
	const onMobileNavigationButtonClick = useCallback(() => {
		onMobileNavigationChange(!mobileNavigationOpen);
		setMobileNavigationOpen(!mobileNavigationOpen);
		if (!mobileNavigationOpen) {
			document.body.style.overflow = "hidden";
		}
	}, [mobileNavigationOpen, setMobileNavigationOpen, onMobileNavigationChange]);
	const onMobileMenuNavigationAreaClickCallback = useCallback(() => {
		onMobileNavigationChange(false);
		setMobileNavigationOpen(false);
		document.body.style.overflow = "";
	}, [onMobileNavigationChange]);
	const preventTouchScrollIfMobileNavigationOpen = useCallback(
		(event: TouchEvent) => {
			if (mobileNavigationOpen) {
				event.preventDefault();
			}
		},
		[mobileNavigationOpen],
	);

	useEffect(() => {
		function handleResize() {
			setMobileNavigationOpen(false);
			onMobileNavigationChange(false);
			document.body.style.overflow = "";
		}
		window.addEventListener("resize", handleResize);
		window.addEventListener("touchmove", preventTouchScrollIfMobileNavigationOpen);
		return () => {
			window.removeEventListener("resize", handleResize);
			window.removeEventListener("touchmove", preventTouchScrollIfMobileNavigationOpen);
		};
	}, [setMobileNavigationOpen, onMobileNavigationChange, preventTouchScrollIfMobileNavigationOpen]);
	useEffect(() => {
		setMobileNavigationOpen(false);
		onMobileNavigationChange(false);
		document.body.style.overflow = "";
	}, [onMobileNavigationChange, pathname, searchParams]);

	const mobileNavigationButton = (
		<MobileNavigationButton
			mobileNavigationOpen={mobileNavigationOpen}
			onMobileNavigationButtonClick={onMobileNavigationButtonClick}
		/>
	);

	const hiddenAtMobileBreakpoint =
		mobileBreakpoint === Breakpoint.xl
			? "xl:hidden"
			: mobileBreakpoint === Breakpoint.lg
			? "lg:hidden"
			: "md:hidden";

	return (
		<>
			<FocusTrap active={mobileNavigationOpen} className={`z-10 ${hiddenAtMobileBreakpoint}`}>
				<div className={`fixed z-10 ${hiddenAtMobileBreakpoint}`} id="navbar_mobile">
					<div
						className={`fixed top-0 bottom-0 left-0 right-0 bg-neutral-600 opacity-50 z-10 cursor-pointer ${hiddenAtMobileBreakpoint} ${
							mobileNavigationOpen ? "block" : "hidden"
						}`}
						onClick={onMobileNavigationButtonClick}
					></div>
					<div
						className={`fixed w-full h-12 z-30 cursor-pointer ${hiddenAtMobileBreakpoint} ${
							mobileNavigationOpen ? "block" : "hidden"
						}`}
						onClick={onMobileNavigationButtonClick}
					></div>
					<div className={`fixed ${hiddenAtMobileBreakpoint} w-full z-20`}>
						<div className="flex items-center justify-between h-12 px-4 bg-[color:var(--epic-nav-bottom-bar-back-color)]">
							{siteIdentityLinkLogo ? (
								<SiteIdentityMobileLogo
									navLogoKind={siteIdentityLinkLogo}
									tooltip={siteIdentityLinkTooltip}
								/>
							) : (
								<div></div>
							)}

							{mobileNavigationButton}
						</div>
					</div>
					<nav
						onClick={onMobileMenuNavigationAreaClickCallback}
						className={`font-title ${
							itemsFontWeight === FontWeight.extraBold ? "font-extrabold" : "font-semibold"
						} fixed ${hiddenAtMobileBreakpoint} pt-12 w-full z-20 max-h-screen overflow-y-auto ${
							mobileNavigationOpen ? "block" : "hidden"
						}`}
					>
						{children}
					</nav>
				</div>
			</FocusTrap>
			<div className={`mt-[48px] block ${hiddenAtMobileBreakpoint}`}></div>
		</>
	);
};
export default MobileNavBar;

